import { ref, computed } from "vue"
import { useAuthStore } from "../pinia-stores/auth.js"
import { Capacitor } from "@capacitor/core"
import { Contacts } from "@capacitor-community/contacts"

export function useCordovaPlugin() {
  const contacts = ref([])
  const selectedContacts = ref([])
  const isNative = computed(() => typeof Capacitor !== "undefined" && Capacitor.isNativePlatform())

  /**
   * The web Contact Picker API only exists in Chromium (Chrome/Android, Edge
   * Android). Safari — including iOS and an iOS installed PWA — does not
   * implement it, so `navigator.contacts` is undefined there.
   */
  const webPickerSupported = computed(
    () =>
      typeof navigator !== "undefined" &&
      Boolean(navigator.contacts) &&
      typeof navigator.contacts.select === "function" &&
      typeof window !== "undefined" &&
      window.isSecureContext !== false,
  )
  // Kept for compatibility with the existing callers' intent.
  const hasContactsApi = webPickerSupported

  // Contacts can be picked when running natively (Capacitor Contacts plugin)
  // or on web/PWA when the Contact Picker API is available (secure context).
  const canUseContacts = computed(() => isNative.value || webPickerSupported.value)

  // The Capacitor plugin and the web Contact Picker API use different
  // projections AND different result shapes.
  const pluginProjection = { name: true, phones: true, emails: true, addresses: false, icons: true }
  // The web API takes an **iterable array of property-name strings**, not an
  // object and not the ContactInfo dicts from the spec draft. Anything else
  // throws "The object must have a callable @@iterator property".
  const webProperties = ["name", "tel", "email", "icon"]
  let webSupportedProperties = null

  /**
   * `select()` throws a TypeError when it is handed a property the browser does
   * not implement (notably `icon`), so the supported set has to be discovered
   * once and filtered first.
   */
  async function getWebProperties() {
    if (webSupportedProperties) return webSupportedProperties
    let supported = webProperties
    try {
      if (typeof navigator.contacts.getProperties === "function") {
        const reported = await navigator.contacts.getProperties()
        if (Array.isArray(reported) && reported.length) {
          supported = webProperties.filter((prop) => reported.includes(prop))
        }
      }
    } catch (ex) {
      console.warn("[contacts] getProperties() failed, using the default property list", ex)
    }
    // Without a name and a number there is nothing to fill.
    webSupportedProperties = supported
    return webSupportedProperties
  }

  async function checkContactsPermission() {
    if (isNative.value) {
      try {
        const permission = await Contacts.checkPermissions()
        if (permission.contacts === "granted") return true
        if (permission.contacts === "denied") return false
        const perm = await Contacts.requestPermissions()
        return perm.contacts === "granted"
      } catch (error) {
        console.error("Contacts permission check failed", error)
        return false
      }
    }
    // The web Contact Picker API is NOT part of the Permissions API —
    // `navigator.permissions.query({ name: "contacts" })` throws. The
    // picker prompts the user itself when called.
    return true
  }

  /**
   * The web Contact Picker returns per property, not per contact: `name` is an
   * array of name parts, `tel`/`email`/`icon` are arrays of strings. Older
   * builds returned `name` as a string or as { firstName, lastName }.
   */
  function normalizeWebName(raw) {
    const value = raw?.name
    if (Array.isArray(value)) {
      const parts = value
        .map((part) => {
          if (typeof part === "string") return { givenName: part, familyName: "" }
          return {
            givenName: part?.givenName || part?.firstName || part?.value || "",
            familyName: part?.familyName || part?.lastName || "",
          }
        })
        .filter((p) => p.givenName || p.familyName)
      return {
        givenName: parts.map((p) => p.givenName).filter(Boolean).join(" "),
        familyName: parts.map((p) => p.familyName).filter(Boolean).join(" "),
      }
    }
    if (value && typeof value === "object") {
      return {
        givenName: value.givenName || value.firstName || "",
        familyName: value.familyName || value.lastName || "",
      }
    }
    // Plain string: split on the last word, which is the most reliable split
    // for the overwhelmingly common "Given Family" shape.
    const words = String(value || "").trim().split(/\s+/).filter(Boolean)
    if (words.length < 2) {
      return { givenName: words[0] || "", familyName: "" }
    }
    return {
      givenName: words.slice(0, -1).join(" "),
      familyName: words[words.length - 1],
    }
  }

  const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : [])

  /**
   * Maps a raw Capacitor-plugin or web-picker contact into the canonical
   * app shape (id, displayName, name.givenName/familyName, phones[].number,
   * emails[].address) so downstream normalization works everywhere.
   */
  function toAppContact(raw) {
    if (!raw) return null

    // Capacitor plugin payload: { contactId, name: { display, given, family },
    // phones: [{ number }], emails: [{ address }], photos: [...] }
    if (raw.contactId || raw.name?.given !== undefined || raw.name?.family !== undefined) {
      return {
        id: raw.contactId || raw.id,
        displayName: raw.name?.display || [raw.name?.given, raw.name?.family].filter(Boolean).join(" "),
        name: {
          givenName: raw.name?.given || "",
          familyName: raw.name?.family || "",
        },
        phones: (raw.phones || []).map((p) => ({ number: p.number })).filter((p) => p.number),
        emails: (raw.emails || []).map((e) => ({ address: e.address })).filter((e) => e.address),
        photos: raw.photos || [],
      }
    }

    // Web Contact Picker payload: { name: [...] | "Full Name", tel: [...],
    // email: [...], icon: [...] }
    if (raw.tel !== undefined || raw.email !== undefined || raw.icon !== undefined || raw.name !== undefined) {
      const name = normalizeWebName(raw)
      const displayName =
        raw.displayName ||
        (name.givenName && name.familyName
          ? `${name.givenName} ${name.familyName}`.trim()
          : name.givenName || name.familyName)
      return {
        id: raw.id,
        displayName,
        name,
        phones: asArray(raw.tel ?? raw.phones)
          .map((tel) => (typeof tel === "string" ? { number: tel } : { number: tel?.number ?? tel?.value }))
          .filter((p) => p.number),
        emails: asArray(raw.email ?? raw.emails)
          .map((mail) => (typeof mail === "string" ? { address: mail } : { address: mail?.address ?? mail?.value }))
          .filter((e) => e.address),
        photos: asArray(raw.icon ?? raw.photos).map((photo) =>
          typeof photo === "string" ? { value: photo } : photo,
        ),
      }
    }

    // Already app-shaped
    return raw
  }

  /**
   * Single entry point for the web picker. Must be called directly from a user
   * gesture: the API throws (or silently rejects) otherwise.
   */
  async function selectWebContacts(opts = { multiple: false }) {
    if (!webPickerSupported.value) {
      throw Object.assign(new Error("Contact Picker API unavailable"), { name: "NotSupportedError" })
    }
    const properties = await getWebProperties()
    const result = await navigator.contacts.select(properties, { multiple: opts.multiple ?? false })
    return asArray(result).map(toAppContact).filter(Boolean)
  }

  // `select()` only works while the user activation from the click is still
  // valid, so the supported-property lookup is warmed up here instead of being
  // discovered for the first time inside the gesture handler. Everything left in
  // `selectWebContacts` is then a resolved-promise continuation.
  if (webPickerSupported.value) {
    getWebProperties().catch(() => {})
  }

  async function getContacts() {
    if (!canUseContacts.value) {
      contacts.value = []
      selectedContacts.value = []
      return []
    }

    if (isNative.value) {
      if (!(await checkContactsPermission())) return []
      try {
        const { contacts: result } = await Contacts.getContacts({ projection: pluginProjection })
        const mapped = (result || []).map(toAppContact).filter(Boolean)
        contacts.value = mapped
        selectedContacts.value = mapped
        return mapped
      } catch (ex) {
        console.error("Error fetching contacts via Capacitor plugin:", ex)
        contacts.value = []
        selectedContacts.value = []
        return []
      }
    }

    try {
      // Web picker: must be called from a user gesture
      const mapped = await selectWebContacts({ multiple: true })
      contacts.value = mapped
      selectedContacts.value = mapped
      return mapped
    } catch (ex) {
      if (ex?.name !== "AbortError") {
        console.error("Error selecting contacts via navigator.contacts:", ex)
      }
      contacts.value = []
      selectedContacts.value = []
      return []
    }
  }

  async function selectContact(opts = { multiple: false }) {
    if (!canUseContacts.value) {
      selectedContacts.value = []
      return []
    }

    if (isNative.value) {
      if (!(await checkContactsPermission())) return []
      try {
        const { contacts: result } = await Contacts.getContacts({ projection: pluginProjection })
        const mapped = (result || []).map(toAppContact).filter(Boolean)
        useAuthStore().setContactList(mapped)
        if (opts.multiple) {
          selectedContacts.value = [...selectedContacts.value, ...mapped]
        } else {
          selectedContacts.value = mapped
        }
        useAuthStore().setSelectedContact(selectedContacts.value)
        return mapped
      } catch (ex) {
        console.error("Error selecting contact via Capacitor plugin:", ex)
        selectedContacts.value = []
        useAuthStore().setSelectedContact([])
        return []
      }
    }

    try {
      // Web picker: must be called from a user gesture
      const mapped = await selectWebContacts({ multiple: opts.multiple ?? false })
      if (opts.multiple) {
        selectedContacts.value = [...selectedContacts.value, ...mapped]
      } else {
        selectedContacts.value = mapped
      }
      useAuthStore().setSelectedContact(selectedContacts.value)
      return mapped
    } catch (ex) {
      console.error("Error selecting contact via navigator.contacts:", ex)
      selectedContacts.value = []
      useAuthStore().setSelectedContact([])
      return []
    }
  }

  /**
   * Side-effect free variant of getContacts(): returns the device contacts
   * without touching `selectedContacts` or the auth store. Used when contacts
   * are only read to prefill a form (e.g. the telephone contact button), where
   * the group-import selection state must stay untouched.
   * Native only: on web the Contact Picker API has no "list all" mode.
   */
  async function listContacts() {
    if (!canUseContacts.value || !isNative.value) return []
    if (!(await checkContactsPermission())) return []
    try {
      const { contacts: result } = await Contacts.getContacts({ projection: pluginProjection })
      return (result || []).map(toAppContact).filter(Boolean)
    } catch (ex) {
      console.error("Error listing contacts via Capacitor plugin:", ex)
      return []
    }
  }

  /**
   * Side-effect free variant of selectContact(): opens the platform contact
   * picker and returns the picked contacts without writing them to the auth
   * store. Must be called directly from a user gesture (the web Contact Picker
   * API requires one).
   *
   * On a browser without the Contact Picker API (Safari/iOS, Firefox) this
   * throws `NotSupportedError` so callers can fall back to importing a vCard
   * or pasted details instead of silently doing nothing.
   */
  async function pickContacts(opts = { multiple: false }) {
    if (isNative.value) return listContacts()
    return selectWebContacts(opts)
  }

  function addContact(contact) {
    if (!contact) return false
    const isSelected = selectedContacts.value.some(c => c.id === contact.id)
    if (!isSelected) {
      selectedContacts.value = [...selectedContacts.value, contact]
      useAuthStore().setSelectedContact(selectedContacts.value)
      return true
    }
    return false
  }

  function removeContact(contact) {
    selectedContacts.value = selectedContacts.value.filter(c => c.id !== contact.id)
    useAuthStore().setSelectedContact(selectedContacts.value)
  }

  function clearSelectedContacts() {
    selectedContacts.value = []
    useAuthStore().setSelectedContact([])
  }

  function isSelected(contact) {
    return selectedContacts.value.some(c => c.id === contact.id)
  }

  function toggleContact(contact) {
    if (isSelected(contact)) {
      removeContact(contact)
    } else {
      addContact(contact)
    }
  }

  function normalizeContact(contact) {
    if (!contact) return null

    const name = contact.name ? `${contact.name.givenName || ''} ${contact.name.familyName || ''}`.trim() : ''
    const phones = contact.phones ? contact.phones.map(p => p.number).filter(Boolean) : []
    const emails = contact.emails ? contact.emails.map(e => e.address).filter(Boolean) : []
    const addresses = contact.addresses ? contact.addresses.map(a => ({
      formatted: a.formatted || '',
      addressLine: a.addressLine || [],
      region: a.region || '',
      postalCode: a.postalCode || '',
      street: a.street || '',
      country: a.country || '',
    })) : []

    return {
      id: contact.id,
      name,
      firstName: contact.name?.givenName || '',
      lastName: contact.name?.familyName || '',
      phones,
      emails,
      emailsSingle: emails[0] || '',
      phonesSingle: phones[0] || '',
      addresses,
      addressesSingle: addresses[0] || null,
    }
  }

  function getSelectedContactsForInvitation() {
    return selectedContacts.value.map(normalizeContact).filter(c => c.name)
  }

  return {
    contacts,
    selectedContacts,
    isNative,
    hasContactsApi,
    webPickerSupported,
    canUseContacts,
    checkContactsPermission,
    getContacts,
    selectContact,
    listContacts,
    pickContacts,
    selectWebContacts,
    addContact,
    removeContact,
    clearSelectedContacts,
    isSelected,
    toggleContact,
    normalizeContact,
    toAppContact,
    getSelectedContactsForInvitation,
  }
}
